import React from 'react';
import { Link } from 'react-router-dom';
import { Apple, Play } from 'lucide-react';
import Logo from './Logo';
import { trackButton } from '../lib/analytics';

// Replace these with the live store URLs once the apps are submitted.
export const APP_STORE_URL = '#'; // App Store: still pending submission
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.emergent.controlhub7482a95b1b';

const isLive = (u) => typeof u === 'string' && u !== '#' && u.length > 1;

export function StoreButtons({ size = 'md', className = '' }) {
  const padding = size === 'lg' ? 'px-6 py-4' : 'px-5 py-3';
  const text = size === 'lg' ? 'text-base' : 'text-sm';
  const sub = size === 'lg' ? 'text-[10px]' : 'text-[9px]';

  const Btn = ({ href, icon, top, bottom, testid, forceDisabled = false }) => {
    const live = isLive(href) && !forceDisabled;
    const baseCls =
      `group inline-flex items-center gap-3 ${padding} bg-white text-black border border-white ` +
      `hover:bg-white/90 transition-all duration-300 ${live ? '' : 'opacity-70 cursor-not-allowed'}`;
    const inner = (
      <>
        {icon}
        <div className="text-left leading-tight">
          <div className={`${sub} font-body uppercase tracking-[0.15em] text-black/60`}>{top}</div>
          <div className={`${text} font-heading tracking-wider`}>{bottom}</div>
        </div>
      </>
    );
    if (!live) {
      return (
        <button
          type="button"
          disabled
          className={baseCls}
          data-testid={testid}
          aria-label={`${top} ${bottom}`}
          onClick={() => trackButton(testid === 'app-store-btn' ? 'app_store_clicked' : 'play_store_clicked', { live: false })}
        >
          {inner}
        </button>
      );
    }
    return (
      <a
        href={href}
        rel="noopener noreferrer"
        className={baseCls}
        data-testid={testid}
        onClick={() => trackButton(testid === 'app-store-btn' ? 'app_store_clicked' : 'play_store_clicked', { live: true, href })}
      >
        {inner}
      </a>
    );
  };

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <Btn
        href={APP_STORE_URL}
        forceDisabled
        icon={<Apple className="w-6 h-6" />}
        top="App Store"
        bottom="Coming Soon"
        testid="app-store-btn"
      />
      <Btn
        href={PLAY_STORE_URL}
        icon={<Play className="w-6 h-6 fill-black" />}
        top="Get it on"
        bottom="Google Play"
        testid="play-store-btn"
      />
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="relative border-t border-white/[0.06] bg-[#020202]" data-testid="site-footer">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[2px] bg-gradient-to-r from-transparent via-[#a83a4a]/40 to-transparent" />
      <div className="container mx-auto max-w-6xl px-6 py-14">
        <div className="grid md:grid-cols-3 gap-10 mb-10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <Logo size={40} glow />
              <span className="font-heading text-base text-white tracking-[0.28em]">RESET</span>
            </div>
            <p className="text-white/35 font-body text-xs leading-relaxed max-w-xs">
              The recovery support app for men reclaiming control. Available on iOS &amp; Android.
            </p>
          </div>

          <div>
            <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.3em] font-body mb-4">Legal</p>
            <ul className="space-y-2.5 font-body text-sm">
              <li><Link to="/privacy" className="text-white/55 hover:text-white transition" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-white/55 hover:text-white transition" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
              <li><Link to="/support" className="text-white/55 hover:text-white transition" data-testid="footer-support">Support</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.3em] font-body mb-4">Get the App</p>
            <StoreButtons size="md" />
          </div>
        </div>

        <div className="pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row justify-between gap-3 text-white/30 text-xs font-body">
          <p>&copy; {new Date().getFullYear()} ResetProtocol. All rights reserved.</p>
          <p>
            <a href="mailto:support@resetprotocol.app" className="hover:text-white/60 transition" data-testid="footer-email">support@resetprotocol.app</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
